import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import ExitToAppIcon from '@mui/icons-material/ExitToApp';

import { Button, FormControl, IconButton, MenuItem, Select, Stack } from "@mui/material";

import SetMasksComponent from "../../../../Mask/Set";

import { api } from "../../../../../utils";
import { IError } from "../../../../../models/ReportPageModels";
import { MaskImagesProvider } from "../../../../../context/MaskImagesContext";

interface AllMasksRequest {
    data: AllMasksData;
    error: IError;
}
interface AllMasksData {
    masks: string[];
}

interface MaskContentRequest {
    data: MaskContentData;
    error: IError;
}
interface MaskContentData {
    grouped: GroupedModesModel;
}
interface GroupedModesModel {
    modes: GroupedSetModel[];
}
export interface GroupedSetModel {
    name: string;
    maskGroups: GroupedMaskGroupModel[];

    additionMask: MaskModel | null;
    damageMask: MaskModel | null;
}
export interface GroupedMaskGroupModel {
    id: string;

    groupMask: MaskModel;
    isSameMasks: boolean;
    masks: MaskModel[];
}
export interface MaskModel {
    bodystyle: string;
    label: string;
    mandatory: string;
    ratio: string;
    lens: string;
    axis: string;
    exposure: string;
    localFilename: string;
    tag: string;
    namingConvention: string;
    sendTo: string;
    subfolder: string;

    // not used
    fStop: string;
    exposureTime: string;
    colour: string;
    HDR: string;
    portait: string;
    portraitOrientation: string;
    flash: string;
    timer: string;
    resize: string;
    inNotification: string;
}

export interface MaskImageModel {
    id: string;
    imagePath: string;
    imageName: string;
    ratio: "16x9" | "4x3" | "3x2" | "16x9 or 4x3";
    vehicleType: "regular" | "motorcycle" | "van";
}

const clearCurrentMask: MaskContentData = {
    grouped: {
        modes: []
    }
}

const specialStructureFile: string[] = ["Norton Way Chiswick"];

export function GetMaskUrl(maskName: string): string {
    var maskfilename = maskName.replaceAll("=", "%3D").replaceAll("+", "%2B");
    if (maskfilename.endsWith(".png"))
        return `https://ios-icc-components.s3.eu-west-1.amazonaws.com/masks/${maskfilename}`;
    return `https://ios-icc-components.s3.eu-west-1.amazonaws.com/masks/${maskfilename}.png`;
}

function EditMaskStructure() {
    const { enqueueSnackbar } = useSnackbar();

    const { mask } = useParams<string>();

    // Create padding in separate tab
    const styles = {
        container: {
            padding: mask !== undefined ? "20px" : "0px"
        }
    }

    const [allMasks, setAllMasks] = useState<string[]>([]);
    const [selectedMask, setSelectedMask] = useState<string>("");

    const [currentMask, setCurrentMask] = useState<MaskContentData>(clearCurrentMask)

    useEffect(() => {
        CheckForDefaultMask();
        GetAllMasks();

        CheckUrlParams();
    }, []);

    function CheckForDefaultMask() {
        const key: string = "maskDefaultMask";
        if (localStorage.getItem(key) === null) {
            var defaultMask: MaskModel = {
                bodystyle: "ALL",
                label: "(none)",
                mandatory: "N",
                ratio: "16:9",
                lens: "1",
                exposure: "0",
                axis: "None",
                localFilename: "-",
                tag: "",
                namingConvention: "<tag>_<seq>",
                sendTo: "Web",
                subfolder: "none",

                // not used
                fStop: "",
                exposureTime: "",
                colour: "",
                HDR: "",
                portait: "",
                portraitOrientation: "",
                flash: "",
                timer: "",
                resize: "",
                inNotification: "",
            }
            localStorage.setItem(key, JSON.stringify(defaultMask));
        }
    }

    const GetAllMasks = async () => {
        try {
            const response: AllMasksRequest = await api.get("api/Admin/GetAllMasks", {
                headers: {
                    "Authorization": localStorage.getItem("token")!
                }
            }).json();

            if (response.error == null) {
                setAllMasks(response.data.masks)
            }
            else {
                enqueueSnackbar(response.error.description, { variant: "error" });
            }
        }
        catch (err) {
            enqueueSnackbar((err as Error).message, { variant: "error" });
        }
    }

    const MaskSelected = async (maskName: string) => {
        setSelectedMask(maskName);

        setCurrentMask(clearCurrentMask);

        if (specialStructureFile.includes(maskName)) {
            alert("This is a 'Special mask structure' that Admin Dashboard currently can't process.");
        }

        try {
            const response: MaskContentRequest = await api.get(`api/Admin/GetMaskContent?maskFilename=${maskName}`, {
                headers: {
                    "Authorization": localStorage.getItem("token")!
                }
            }).json();

            if (response.error == null) {
                setCurrentMask(response.data)
            }
            else {
                enqueueSnackbar(response.error.description, { variant: "error" });
            }
        }
        catch (err) {
            enqueueSnackbar((err as Error).message, { variant: "error" });
        }
    }

    const SaveMaskSet = async (_event: any) => {
        try {

            const response: AllMasksRequest = await api.post(`api/Admin/SaveMaskSet?maskFilename=${selectedMask}`, {
                json: currentMask,
                headers: {
                    "Authorization": localStorage.getItem("token")!
                }
            }).json();

            if (response.error === null) {
                enqueueSnackbar("Masks saved successfully", { variant: "success" });
            }
            else {
                enqueueSnackbar(response.error.description, { variant: "error" });
            }
        }
        catch (err) {
            enqueueSnackbar((err as Error).message, { variant: "error" });
        }
    }

    function AddMaskSet() {
        var newSet: GroupedSetModel = {
            name: "New set",
            maskGroups: [],

            additionMask: null,
            damageMask: null
        }

        currentMask.grouped.modes.push(newSet);
        setCurrentMask({ ...currentMask });
    }

    function RemoveMaskSet(set: GroupedSetModel) {
        var index = currentMask.grouped.modes.indexOf(set);
        if (index > -1) {
            currentMask.grouped.modes.splice(index, 1);
            setCurrentMask({ ...currentMask });
        }
    }

    function OpenMaskEditInSeparateTab() {
        var url = "https://admindash.auto.works/maskEdit/";
        if (selectedMask !== "")
            url += selectedMask;
        else
            url += "-"
        window.open(url, "_blank", "noreferrer");
    }

    function CheckUrlParams() {
        // from Admin page
        if (mask === undefined)
            return;
        // from Separate tab
        if (mask === "-") // no mask selected
            return;
        // load mask
        MaskSelected(mask)
    }

    return (
        <Stack className="container" style={styles.container}>
            <Stack direction={"row"}>
                <FormControl sx={{ width: 245 }}>
                    <Select
                        value={selectedMask}
                        onChange={(event: any) => MaskSelected(event.target.value)}
                        size="small"
                        defaultValue={"Select mask"}>
                        {allMasks.map((mask, index) =>
                            <MenuItem
                                key={index}
                                value={mask}>
                                {mask}
                            </MenuItem>
                        )}
                    </Select>
                </FormControl>
                <Button variant="outlined"
                    onClick={SaveMaskSet}
                    sx={{ width: '42px', marginLeft: '10px' }}>
                    Save
                </Button>
                <IconButton
                    color="primary"
                    sx={{ marginLeft: "20px" }}
                    onClick={() => { OpenMaskEditInSeparateTab() }}>
                    <ExitToAppIcon sx={{ fontSize: 26 }} />
                </IconButton>
            </Stack>


            {currentMask !== undefined ?
                <>
                    <MaskImagesProvider>
                        {(currentMask.grouped.modes ?? []).map((item, index) => (
                            <SetMasksComponent
                                key={index}
                                setModel={item}
                                removeSet={RemoveMaskSet} />
                        ))}
                    </MaskImagesProvider>

                    <Button variant="outlined"
                        onClick={AddMaskSet}
                        sx={{ width: '100px', marginTop: '10px' }}>
                        Add set
                    </Button>
                </>
                : null}
        </Stack>
    )
}

export default EditMaskStructure;